// Image pools used for blog articles.
//
// `categoryImages` is the legacy 1-image-per-category fallback used as the
// hero / Open-Graph image when an article doesn't declare its own.
//
// `categorySectionImages` is a NEW pool of 3-4 photos per category that
// get rotated *inside* each article — between sections — to make every
// post feel visually rich without rewriting content. Articles pick the
// rotation deterministically from their `id`, so the same article always
// shows the same images (good for SEO + shared links).
//
// All URLs force a 1200x675 (16:9) crop and WebP format so they're
// identical in aspect ratio on the site AND when shared, while saving
// ~30-40% bandwidth vs JPEG with no perceptible quality loss.
const categoryImages = {
  Habits:
    "https://images.unsplash.com/photo-1599950753725-ea5d8aba0d29?crop=entropy&cs=srgb&fm=webp&w=1200&h=675&fit=crop",
  Mindset:
    "https://images.unsplash.com/photo-1531322804667-8d2907f41406?crop=entropy&cs=srgb&fm=webp&w=1200&h=675&fit=crop",
  Gamification:
    "https://images.unsplash.com/photo-1542751371-adc38448a05e?crop=entropy&cs=srgb&fm=webp&w=1200&h=675&fit=crop",
  "Skill Trees":
    "https://images.unsplash.com/photo-1674027215016-0a4abfdbf1cc?crop=entropy&cs=srgb&fm=webp&w=1200&h=675&fit=crop",
  "Four Worlds":
    "https://images.unsplash.com/photo-1769238506848-b9bef50c5aab?crop=entropy&cs=srgb&fm=webp&w=1200&h=675&fit=crop",
  Skills:
    "https://images.unsplash.com/photo-1531297484001-80022131f5a1?crop=entropy&cs=srgb&fm=webp&w=1200&h=675&fit=crop",
  Community:
    "https://images.unsplash.com/photo-1529070538774-1843cb3265df?crop=entropy&cs=srgb&fm=webp&w=1200&h=675&fit=crop",
  Apps:
    "https://images.unsplash.com/photo-1588177806780-51d021f6b2d2?crop=entropy&cs=srgb&fm=webp&w=1200&h=675&fit=crop",
  "Q&A":
    "https://images.unsplash.com/photo-1499209974431-9dddcece7f88?crop=entropy&cs=srgb&fm=webp&w=1200&h=675&fit=crop",
  Comparisons:
    "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=webp&w=1200&h=675&fit=crop",
  Guides:
    "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?crop=entropy&cs=srgb&fm=webp&w=1200&h=675&fit=crop",
};

// Rotation pool — 3-4 contextually-relevant Unsplash images per category.
// Each entry already carries the 1200x675 WebP crop directive so layout
// never shifts when these load.
const u = (id) =>
  `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=webp&w=1200&h=675&fit=crop`;

const categorySectionImages = {
  Habits: [
    u('photo-1517021897933-0e0319cfbc28'), // morning routine cup of coffee, journal
    u('photo-1483794344563-d27a8d18014e'), // misty path / forest
    u('photo-1506784983877-45594efa4cbe'), // calendar planner
    u('photo-1506905925346-21bda4d32df4'), // mountain sunrise / consistency
  ],
  Mindset: [
    u('photo-1499209974431-9dddcece7f88'), // person at desk, thinking
    u('photo-1527436826045-8805c615a6df'), // looking out a window
    u('photo-1455390582262-044cdead277a'), // stationery + coffee
    u('photo-1506905925346-21bda4d32df4'), // sunrise summit
  ],
  Gamification: [
    u('photo-1511512578047-dfb367046420'), // game controller / xp / progress
    u('photo-1542751371-adc38448a05e'),     // arcade neon
    u('photo-1592155931584-901ac15763e3'),  // game pieces, levels
    u('photo-1552820728-8b83bb6b773f'),     // colourful blocks / progress
  ],
  "Skill Trees": [
    u('photo-1500322969630-a26ab6eb64cc'), // trees / growth metaphor
    u('photo-1519681393784-d120267933ba'), // mountains layered
    u('photo-1466692476868-aef1dfb1e735'), // tall trees from below
    u('photo-1473773508845-188df298d2d1'), // path through forest
  ],
  "Four Worlds": [
    u('photo-1506905925346-21bda4d32df4'), // sunrise mountains
    u('photo-1500382017468-9049fed747ef'), // four-quadrant landscape
    u('photo-1469474968028-56623f02e42e'), // expansive landscape
    u('photo-1519681393784-d120267933ba'), // four mountain peaks
  ],
  Skills: [
    u('photo-1531297484001-80022131f5a1'), // typing / craft / focus
    u('photo-1455390582262-044cdead277a'), // notebook + pen
    u('photo-1483058712412-4245e9b90334'), // workspace / laptop
    u('photo-1542435503-956c469947f6'),    // pottery / hands working
  ],
  Community: [
    u('photo-1529070538774-1843cb3265df'), // group outdoors
    u('photo-1543269865-cbf427effbad'),    // people meeting
    u('photo-1517245386807-bb43f82c33c4'), // shared workspace
    u('photo-1511632765486-a01980e01a18'), // hands together
  ],
  Apps: [
    u('photo-1588177806780-51d021f6b2d2'), // phone in hand
    u('photo-1512941937669-90a1b58e7e9c'), // phone screen
    u('photo-1611162616305-c69b3fa7fbe0'), // phone wellness app
    u('photo-1556761175-5973dc0f32e7'),    // app interface
  ],
  "Q&A": [
    u('photo-1454165804606-c3d57bc86b40'), // notebook / questions
    u('photo-1499209974431-9dddcece7f88'), // person + journal
    u('photo-1455390582262-044cdead277a'), // stationary
    u('photo-1481627834876-b7833e8f5570'), // open book
  ],
  Comparisons: [
    u('photo-1551288049-bebda4e38f71'),    // laptop comparison
    u('photo-1556761175-5973dc0f32e7'),    // app interface
    u('photo-1611162616305-c69b3fa7fbe0'), // phone wellness app
    u('photo-1517245386807-bb43f82c33c4'), // workspace
  ],
  Guides: [
    u('photo-1481627834876-b7833e8f5570'), // open book
    u('photo-1455390582262-044cdead277a'), // notebook + pen
    u('photo-1483058712412-4245e9b90334'), // workspace / laptop
    u('photo-1499209974431-9dddcece7f88'), // person at desk, thinking
  ],
};

export const getArticleImage = (article) => {
  if (!article) return null;
  return article.image || categoryImages[article.category] || categoryImages.Habits;
};

/**
 * Build a responsive `srcset` from an Unsplash URL by overriding the
 * `w` and `h` query params at multiple breakpoints. Pairs with a
 * `sizes` attribute on the <img>. Mobile browsers will request the
 * 480w variant (~50KB) instead of the 1200w hero (~150KB) — typical
 * 60-70% bandwidth saving without any layout change.
 *
 * Returns { src, srcSet, sizes } or null if the input isn't an
 * Unsplash URL we know how to rewrite.
 *
 * Use it as:
 *   const r = getResponsiveSrc(getArticleImage(article));
 *   <img src={r.src} srcSet={r.srcSet} sizes={r.sizes} ... />
 */
export const getResponsiveSrc = (url, opts = {}) => {
  if (!url || typeof url !== 'string') return null;
  if (!url.includes('images.unsplash.com')) {
    return { src: url, srcSet: undefined, sizes: undefined };
  }
  // Article cards in BlogIndex render at ~400px wide on desktop, ~340px
  // wide on mobile (1-col), and 1200px on the featured hero. The widths
  // below cover @1x, @2x and @3x for retina screens at all common
  // viewport sizes.
  const widths = opts.widths || [400, 600, 800, 1200];
  const buildAt = (w) =>
    url.replace(/[?&]w=\d+/, '').replace(/[?&]h=\d+/, '') +
    (url.includes('?') ? '&' : '?') +
    `w=${w}&h=${Math.round(w * 9 / 16)}`;
  const srcSet = widths.map((w) => `${buildAt(w)} ${w}w`).join(', ');
  // Default sizes pattern for the blog grid:
  //   1 col mobile, 2 cols sm, 3 cols lg → ~33vw on big screens
  const sizes =
    opts.sizes ||
    '(max-width: 640px) 92vw, (max-width: 1024px) 46vw, 30vw';
  return { src: url, srcSet, sizes };
};

/**
 * Pick a deterministic image from the category section pool.
 * Same article always renders the same image at the same offset, so the
 * page is stable across reloads and SEO crawlers.
 */
export const getSectionImage = (article, offset = 0) => {
  if (!article) return null;
  const pool =
    categorySectionImages[article.category] || categorySectionImages.Habits;
  if (!pool || pool.length === 0) return null;
  const seed = (article.id || 0) + offset;
  return pool[seed % pool.length];
};

export default categoryImages;
